import React from "react"
import { useTranslation } from "react-i18next"
import { faClose, faCopy, faKey, faReceipt } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import ModalPortal from "../../../../components/Modal/ModalPortal"
import Animate, { AnimationTypes } from "../../../../components/Animate/Animate"
import defaultStyle from "../../../../styles/defaultPageStyle.module.css"
import { TitlePageResponsiveIconSmall } from "../../../../components/Titles/Titles"
import { ButtonIcon } from "../../../../components/Button/Button"
import InputField from "../../../../components/InputField/InputField"
import { GenericDropDown } from "../../../../components/Dropdown/Dropdown"
import { SimpleToggle } from "../../../../components/SimpleToggle/SimpleToggle"
import { PopupHelpButton } from "../../../../components/Help/PopupHelpButton"
import THEME from "../../../../configuration/Theme"
import { SIZE } from "../../../../configuration/Sizes"
import { EFiscalEnvironment } from "../../../../Objects/FiscalIntegration/EFiscalEnvironment"
import { EFiscalTaxRegime } from "../../../../Objects/FiscalIntegration/EFiscalTaxRegime"
import { type ISaveFiscalIntegrationSettings } from "../../../../Objects/FiscalIntegration/ISaveFiscalIntegrationSettings"
import GetSpace from "../../../../utils/space"

type Props = {
  isOpen: boolean
  settings: ISaveFiscalIntegrationSettings
  onChange: (settings: ISaveFiscalIntegrationSettings) => void
  onClose: () => void
  onSave: () => void
  onTest: () => void
  issuerConfigured: boolean
  onConfigureIssuer: () => void
}

export const FiscalSettingsPopup: React.FC<Props> = ({
  isOpen,
  settings,
  onChange,
  onClose,
  onSave,
  onTest,
  issuerConfigured,
  onConfigureIssuer,
}) => {
  const { t } = useTranslation(["fiscalIntegration", "buttons", "helpPopup"])

  if (!isOpen) {
    return null
  }

  const patch = (next: Partial<ISaveFiscalIntegrationSettings>) => onChange({ ...settings, ...next })

  const activeTokenReady =
    settings.environment === EFiscalEnvironment.Homologation
      ? settings.hasHomologationToken || Boolean(settings.homologationToken?.trim())
      : settings.hasProductionToken || Boolean(settings.productionToken?.trim())

  const copyWebhook = () => void navigator.clipboard.writeText(settings.webhookUrl)

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div className={defaultStyle.alert}>
            <TitlePageResponsiveIconSmall
              icon={faReceipt}
              iconColor={THEME.colors.iconBGColor}
              title={t("settings.title")}
            >
              <div className={`${defaultStyle.ButtonsActions} gap-1`}>
                <PopupHelpButton
                  variant="global"
                  helpKey="pages.myrestaurant.fiscalintegration.settings.1"
                  title={t("helpPopup:global.fiscalIntegrationSettings.title")}
                  description={t("helpPopup:global.fiscalIntegrationSettings.description")}
                  steps={t("helpPopup:global.fiscalIntegrationSettings.steps", { returnObjects: true }) as string[]}
                  bullets={t("helpPopup:global.fiscalIntegrationSettings.bullets", { returnObjects: true }) as string[]}
                />

                <ButtonIcon
                  sizeNumber={SIZE.buttonCloseIconSize}
                  bgColor={THEME.colors.buttonBGCloseColor}
                  iconColor={THEME.colors.buttonFGCloseColor}
                  icon={faClose}
                  onClick={onClose}
                  size="small"
                  descriptionKey="interactionDescriptions:pages.myRestaurant.fiscalIntegration.components.fiscalSettingsPopup.button.onClose"
                />
              </div>
            </TitlePageResponsiveIconSmall>

            <div className="p-2">
              <Animate type={AnimationTypes.fadeInTraslate}>
                <section className="rounded-xl border-2 border-sky-100 bg-sky-50 p-3 text-xs leading-relaxed text-sky-900">
                  <strong className="block text-sm">{t("settings.securityTitle")}</strong>

                  <p className="mt-1">{t("settings.securityDescription")}</p>
                </section>
              </Animate>

              <GetSpace top={5} />

              {!issuerConfigured && (
                <Animate type={AnimationTypes.fadeInTraslate} duration="1.2s">
                  <section className="rounded-xl border-2 border-red-300 bg-red-50 p-3 text-xs text-red-900">
                    <strong className="block text-sm">{t("settings.issuerRequiredTitle")}</strong>

                    <p className="mt-1">{t("settings.issuerRequiredDescription")}</p>

                    <button
                      type="button"
                      onClick={onConfigureIssuer}
                      className="mt-2 rounded-lg bg-red-700 px-3 py-2 font-bold text-white hover:bg-red-800"
                      data-interaction-description-key="interactionDescriptions:pages.myRestaurant.fiscalIntegration.components.fiscalSettingsPopup.button.onConfigureIssuer"
                    >
                      {t("actions.configureIssuer")}
                    </button>
                  </section>
                </Animate>
              )}

              <GetSpace top={5} />

              <section className="p-3 ">
                <GenericDropDown
                  legend={t("settings.environment")}
                  initialState={String(settings.environment)}
                  data={[
                    { value: String(EFiscalEnvironment.Homologation), label: t("environment.homologation") },
                    { value: String(EFiscalEnvironment.Production), label: t("environment.production") },
                  ]}
                  handlerFuncion={(event) => patch({ environment: Number(event.target.value) as EFiscalEnvironment })}
                  descriptionKey="interactionDescriptions:pages.myRestaurant.fiscalIntegration.components.fiscalSettingsPopup.select.environment"
                />

                <GetSpace top={10} />

                <GenericDropDown
                  legend={t("settings.taxRegime")}
                  initialState={String(settings.taxRegime)}
                  data={[
                    { value: String(EFiscalTaxRegime.SimpleNational), label: t("taxRegime.simpleNational") },
                    {
                      value: String(EFiscalTaxRegime.SimpleNationalExcess),
                      label: t("taxRegime.simpleNationalExcess"),
                    },
                    { value: String(EFiscalTaxRegime.Normal), label: t("taxRegime.normal") },
                    {
                      value: String(EFiscalTaxRegime.MicroEntrepreneur),
                      label: t("taxRegime.microEntrepreneur"),
                    },
                  ]}
                  handlerFuncion={(event) => patch({ taxRegime: Number(event.target.value) as EFiscalTaxRegime })}
                  descriptionKey="interactionDescriptions:pages.myRestaurant.fiscalIntegration.components.fiscalSettingsPopup.select.taxRegime"
                />

                <GetSpace top={10} />

                <SimpleToggle
                  title={t("settings.enabled")}
                  value={settings.isEnabled}
                  onChange={(value) => patch({ isEnabled: value })}
                  descriptionKey="interactionDescriptions:pages.myRestaurant.fiscalIntegration.components.fiscalSettingsPopup.toggle.enabled"
                />

                <GetSpace top={5} />

                <SimpleToggle
                  title={t("settings.automatic")}
                  value={settings.issueAutomatically}
                  onChange={(value) => patch({ issueAutomatically: value })}
                  descriptionKey="interactionDescriptions:pages.myRestaurant.fiscalIntegration.components.fiscalSettingsPopup.toggle.automatic"
                />

                <GetSpace top={5} />

                <SimpleToggle
                  title={t("settings.printAutomatically")}
                  value={settings.printAutomatically}
                  onChange={(value) => patch({ printAutomatically: value })}
                  descriptionKey="interactionDescriptions:pages.myRestaurant.fiscalIntegration.components.fiscalSettingsPopup.toggle.printAutomatically"
                />

                <GetSpace top={10} />

                <div className="space-y-2">
                  <InputField
                    type="password"
                    icon={faKey}
                    autoComplete="off"
                    id="homologation-Id"
                    maxLength={512}
                    value={settings.homologationToken ?? ""}
                    placeholder={
                      settings.hasHomologationToken
                        ? t("settings.tokenConfigured", { environment: t("environment.homologation") })
                        : t("settings.homologationToken")
                    }
                    onChange={(event) =>
                      patch({ homologationToken: event.target.value, clearHomologationToken: false })
                    }
                    descriptionKey="interactionDescriptions:pages.myRestaurant.fiscalIntegration.components.fiscalSettingsPopup.input.tokenConfigured"
                  />

                  <GetSpace top={10} />

                  {settings.hasHomologationToken && (
                    <Animate type={AnimationTypes.fadeInTraslate}>
                      <button
                        type="button"
                        className="text-xs font-bold text-red-600 hover:text-red-800"
                        onClick={() =>
                          patch({
                            homologationToken: "",
                            hasHomologationToken: false,
                            clearHomologationToken: true,
                          })
                        }
                        data-interaction-description-key="interactionDescriptions:pages.myRestaurant.fiscalIntegration.components.fiscalSettingsPopup.button.patch"
                      >
                        {t("settings.removeHomologationToken")}
                      </button>
                    </Animate>
                  )}
                </div>

                <GetSpace top={10} />

                <div className="space-y-2">
                  <InputField
                    type="password"
                    icon={faKey}
                    id="production-id"
                    autoComplete="off"
                    maxLength={512}
                    value={settings.productionToken ?? ""}
                    placeholder={
                      settings.hasProductionToken
                        ? t("settings.tokenConfigured", { environment: t("environment.production") })
                        : t("settings.productionToken")
                    }
                    onChange={(event) => patch({ productionToken: event.target.value, clearProductionToken: false })}
                    descriptionKey="interactionDescriptions:pages.myRestaurant.fiscalIntegration.components.fiscalSettingsPopup.input.tokenConfigured2"
                  />

                  <GetSpace top={10} />

                  {settings.hasProductionToken && (
                    <Animate type={AnimationTypes.fadeInTraslate}>
                      <button
                        type="button"
                        className="text-xs font-bold text-red-600 hover:text-red-800"
                        onClick={() =>
                          patch({
                            productionToken: "",
                            hasProductionToken: false,
                            clearProductionToken: true,
                          })
                        }
                        data-interaction-description-key="interactionDescriptions:pages.myRestaurant.fiscalIntegration.components.fiscalSettingsPopup.button.patch2"
                      >
                        {t("settings.removeProductionToken")}
                      </button>
                    </Animate>
                  )}
                </div>

                <GetSpace top={10} />

                <div className="md:col-span-2">
                  <InputField
                    type="link"
                    value={settings.webhookUrl}
                    placeholder={t("settings.webhook")}
                    disable
                    onChange={() => undefined}
                    descriptionKey="interactionDescriptions:pages.myRestaurant.fiscalIntegration.components.fiscalSettingsPopup.input.webhook"
                  />

                  <GetSpace top={10} />

                  <button
                    type="button"
                    onClick={copyWebhook}
                    className="mt-2 inline-flex items-center gap-2 text-xs font-bold text-sky-700 hover:text-sky-900"
                    data-interaction-description-key="interactionDescriptions:pages.myRestaurant.fiscalIntegration.components.fiscalSettingsPopup.button.copyWebhook"
                  >
                    <FontAwesomeIcon icon={faCopy} />
                    {t("settings.copyWebhook")}
                  </button>
                </div>
              </section>

              <div className="flex flex-col-reverse mt-5 justify-end gap-2 sm:flex-row">
                <button
                  type="button"
                  onClick={onTest}
                  disabled={!activeTokenReady || !issuerConfigured}
                  className="rounded-xl border-2 border-sky-300 bg-sky-50 px-4 py-2 text-sm font-bold text-sky-700 disabled:cursor-not-allowed disabled:opacity-50"
                  data-interaction-description-key="interactionDescriptions:pages.myRestaurant.fiscalIntegration.components.fiscalSettingsPopup.button.onTest"
                >
                  {t("settings.test")}
                </button>

                <button
                  type="button"
                  onClick={onSave}
                  disabled={settings.isEnabled && (!activeTokenReady || !issuerConfigured)}
                  className="rounded-xl bg-emerald-600 px-4 py-2 text-sm font-bold text-white disabled:cursor-not-allowed disabled:bg-slate-300"
                  data-interaction-description-key="interactionDescriptions:pages.myRestaurant.fiscalIntegration.components.fiscalSettingsPopup.button.onSave"
                >
                  {t("buttons:button_save")}
                </button>
              </div>
            </div>
          </div>
        </Animate>
      </div>
    </ModalPortal>
  )
}
